/**
 * ContentContextProvider - Global context management for content creation
 *
 * This provider manages the unified content context used across:
 * - Chat conversations
 * - Write page / content pieces
 * - AI feedback features (tone fit, rewrite, fact check, document analysis)
 *
 * Key Features:
 * - Single source of truth for context state
 * - Project-level defaults with document-level overrides
 * - Persists context across page navigation
 * - Syncs with database when documents are saved
 */

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import type { ContentContext, DocumentContext, Project, ResolvedContext } from '@/types/context';
import { resolveContext, emptyContext, mergeContexts, toChatContext } from '@/types/context';
import type { ChatContext } from '@/api/chat';

// ============================================================================
// Types
// ============================================================================

interface ContentContextState {
  /** Current project (if any) */
  activeProject: Project | null;

  /** Current document ID (if editing existing content) */
  activeDocumentId: string | null;

  /** Current conversation ID (if in chat context) */
  activeConversationId: string | null;

  /** Document-level context overrides (takes precedence over project defaults) */
  documentContext: ContentContext;

  /** Resolved/effective context (project defaults merged with document overrides) */
  effectiveContext: ContentContext;

  /** Resolution details showing what's inherited vs overridden */
  resolution: ResolvedContext | null;

  /** Whether context has been modified since last save */
  isDirty: boolean;
}

interface ContentContextActions {
  /** Set the active project (loads project defaults) */
  setActiveProject: (project: Project | null) => void;

  /** Set the active document ID */
  setActiveDocumentId: (id: string | null) => void;

  /** Set the active conversation ID */
  setActiveConversationId: (id: string | null) => void;

  /** Update document-level context (partial update) */
  updateContext: (updates: Partial<ContentContext>) => void;

  /** Set entire document context (replaces current) */
  setDocumentContext: (context: ContentContext) => void;

  /** Reset document context to project defaults (clear all overrides) */
  resetToProjectDefaults: () => void;

  /** Clear all context state */
  clearContext: () => void;

  /** Get context in ChatContext format (for API calls) */
  getChatContext: (documentFields?: { currentContent?: string; selectedText?: string }) => ChatContext;

  /** Mark context as saved (clears dirty flag) */
  markAsSaved: () => void;
}

type ContentContextValue = ContentContextState & ContentContextActions;

// ============================================================================
// Context
// ============================================================================

const ContentContextContext = createContext<ContentContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface ContentContextProviderProps {
  children: React.ReactNode;
}

export function ContentContextProvider({ children }: ContentContextProviderProps) {
  // Core state
  const [activeProject, setActiveProjectState] = useState<Project | null>(null);
  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [documentContext, setDocumentContextState] = useState<ContentContext>(emptyContext());
  const [isDirty, setIsDirty] = useState(false);

  // Compute project default context
  const projectDefaultContext = useMemo((): ContentContext | null => {
    if (!activeProject) return null;
    return {
      writingStyleId: activeProject.defaultWritingStyleId,
      brandProfileId: activeProject.defaultBrandProfileId,
      purpose: activeProject.defaultPurpose,
      channel: activeProject.defaultChannel,
      targetAudience: activeProject.defaultTargetAudience,
      seoKeywords: activeProject.defaultSeoKeywords,
      storyFramework: activeProject.defaultStoryFramework,
    };
  }, [activeProject]);

  // Compute resolved context (project defaults + document overrides)
  const resolution = useMemo((): ResolvedContext | null => {
    const resolved = resolveContext(projectDefaultContext, documentContext);
    if (activeProject) {
      resolved.projectId = activeProject.id;
    }
    return resolved;
  }, [projectDefaultContext, documentContext, activeProject]);

  // Effective context is the merged result
  const effectiveContext = useMemo((): ContentContext => {
    return resolution?.effective || documentContext;
  }, [resolution, documentContext]);

  // ============================================================================
  // Actions
  // ============================================================================

  const setActiveProject = useCallback((project: Project | null) => {
    setActiveProjectState(project);
    // Don't clear document context when changing projects - let user decide
  }, []);

  const updateContext = useCallback((updates: Partial<ContentContext>) => {
    setDocumentContextState(prev => mergeContexts(prev, updates));
    setIsDirty(true);
  }, []);

  const setDocumentContext = useCallback((context: ContentContext) => {
    setDocumentContextState(context);
    setIsDirty(true);
  }, []);

  const resetToProjectDefaults = useCallback(() => {
    setDocumentContextState(emptyContext());
    setIsDirty(true);
  }, []);

  const clearContext = useCallback(() => {
    setActiveProjectState(null);
    setActiveDocumentId(null);
    setActiveConversationId(null);
    setDocumentContextState(emptyContext());
    setIsDirty(false);
  }, []);

  const getChatContext = useCallback((documentFields?: {
    currentContent?: string;
    selectedText?: string;
  }): ChatContext => {
    return toChatContext(effectiveContext, {
      ...documentFields,
      contentId: activeDocumentId || undefined,
    });
  }, [effectiveContext, activeDocumentId]);

  const markAsSaved = useCallback(() => {
    setIsDirty(false);
  }, []);

  // ============================================================================
  // Context Value
  // ============================================================================

  const value = useMemo((): ContentContextValue => ({
    // State
    activeProject,
    activeDocumentId,
    activeConversationId,
    documentContext,
    effectiveContext,
    resolution,
    isDirty,

    // Actions
    setActiveProject,
    setActiveDocumentId,
    setActiveConversationId,
    updateContext,
    setDocumentContext,
    resetToProjectDefaults,
    clearContext,
    getChatContext,
    markAsSaved,
  }), [
    activeProject,
    activeDocumentId,
    activeConversationId,
    documentContext,
    effectiveContext,
    resolution,
    isDirty,
    setActiveProject,
    updateContext,
    setDocumentContext,
    resetToProjectDefaults,
    clearContext,
    getChatContext,
    markAsSaved,
  ]);

  return (
    <ContentContextContext.Provider value={value}>
      {children}
    </ContentContextContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access the global content context.
 * Must be used within a ContentContextProvider.
 */
export function useContentContext(): ContentContextValue {
  const context = useContext(ContentContextContext);
  if (!context) {
    throw new Error('useContentContext must be used within a ContentContextProvider');
  }
  return context;
}

/**
 * Hook to get just the effective context (for components that only need to read).
 */
export function useEffectiveContext(): ContentContext {
  const { effectiveContext } = useContentContext();
  return effectiveContext;
}

/**
 * Hook to get context in ChatContext format (for API calls).
 */
export function useChatContextFormat(documentFields?: {
  currentContent?: string;
  selectedText?: string;
}): ChatContext {
  const { getChatContext } = useContentContext();
  return useMemo(() => getChatContext(documentFields), [getChatContext, documentFields]);
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Hook to check if a specific context field is inherited from project.
 */
export function useIsFieldInherited(field: keyof ContentContext): boolean {
  const { resolution } = useContentContext();
  if (!resolution) return false;
  return field in resolution.inherited && !(field in resolution.overridden);
}

/**
 * Hook to get the source of a context field value.
 */
export function useContextFieldSource(field: keyof ContentContext): 'project' | 'document' | 'none' {
  const { resolution, documentContext } = useContentContext();

  if (!resolution) {
    return documentContext[field] ? 'document' : 'none';
  }

  if (field in resolution.overridden) return 'document';
  if (field in resolution.inherited) return 'project';
  return 'none';
}
